import { Dialog, DialogContent } from '@/shad/ui/dialog.tsx'
import axios from 'axios'
import { useEffect, useState } from 'react'
import heic2any from 'heic2any'
import { DetailsIcon, ImagePlaceHolderIcon } from '../icons/general'
import { cn } from '../utilities'
import DocxViewer from './DocxViewer'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'

interface IImage {
  path?: string
  src?: string
  alt?: string
  width?: number | string
  allowZoom?: boolean
  className?: string
  widthIframe?: string | number
  heightIframe?: string | number
}

const Image: React.FC<IImage> = ({
  path,
  src,
  alt,
  width,
  allowZoom = false,
  widthIframe = '800px',
  heightIframe = '600px',
  className,
}) => {
  const [image, setImage] = useState<string>()
  const [isZoomed, setIsZoomed] = useState(false)
  const { fireAlert } = useAppAlert()
  const [loading, setLoading] = useState<boolean>(false)

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || ''
  }

  const isFileType = (types: string[]): boolean => {
    if (!path) return false
    const extension = getFileExtension(path)
    return types.includes(extension)
  }

  const isDocument = (): boolean => {
    return isFileType(['pdf', 'docx'])
  }

  const fetchImage = async (path: string, showError: boolean = false) => {
    if (!path) return

    if (showError) setLoading(true)

    try {
      const response = await axios.get(`download-file?path=${path}`, {
        responseType: 'blob',
      })

      if (showError) setLoading(false)

      const contentType = response.data.type

      if (contentType === 'application/pdf') {
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' })
        setImage(URL.createObjectURL(pdfBlob))
        return
      }

      if (contentType === 'image/heic') {
        heic2any({ blob: response.data }).then((convertedBlob) => {
          const blob = Array.isArray(convertedBlob)
            ? convertedBlob[0]
            : convertedBlob
          setImage(URL.createObjectURL(blob))
        })
        return
      }

      setImage(URL.createObjectURL(response.data))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setImage(undefined)
      setLoading(false)
      setIsZoomed(false)
      if (showError) {
        fireAlert({
          variant: 'error',
          message:
            'This file is no longer available. Please have Applicant reupload.',
        })
      }
    }
  }

  useEffect(() => {
    if (path && !image) {
      fetchImage(path)
    }
  }, [path])

  return (
    <>
      <div
        className="relative inline-block cursor-pointer"
        onClick={() => {
          if (isDocument() && path) {
            fetchImage(path, true)
          } else if (!image && path) fetchImage(path, true)
          setIsZoomed(true)
        }}
      >
        {(isDocument() && !image) || (!isDocument() && !src && !image) ? (
          <div
            className={cn(
              className ? className : 'max-w-full h-auto',
              'flex justify-center items-center border rounded-lg'
            )}
          >
            {!isDocument() ? (
              <ImagePlaceHolderIcon className="text-7xl text-black" />
            ) : (
              <DetailsIcon className="text-7xl text-black" />
            )}
          </div>
        ) : null}
        {(src || image) &&
          (isDocument() ? (
            <iframe
              src={src ?? image}
              width={widthIframe}
              height={heightIframe}
              className={className ? className : 'max-w-full'}
              title={alt}
            />
          ) : (
            <img
              src={src ?? image}
              alt={alt}
              width={width}
              className={className ? className : 'max-w-full h-auto'}
            />
          ))}
        {allowZoom && (
          <div
            className="absolute top-2 right-2 py-1 px-2 bg-white hover:bg-white/90 rounded-full text-sm font-medium transition-colors"
            aria-label="Zoom image"
          >
            {!isDocument() ? 'Zoom' : 'View'}
          </div>
        )}
      </div>

      <Dialog
        open={isZoomed || loading}
        onOpenChange={() => {
          setIsZoomed(false)
          setLoading(false)
        }}
      >
        <DialogContent
          className={cn(
            'max-w-screen-lg overflow-y-auto max-h-screen w-full p-1',
            isDocument() && !loading ? ' h-5/6' : ''
          )}
        >
          {loading && !image ? (
            <div className="flex justify-center items-center h-[300px]">
              <EllipsesLoader size="md" />
            </div>
          ) : isFileType(['pdf']) ? (
            <object
              data={image}
              type="application/pdf"
              className="w-full h-full"
            ></object>
          ) : isFileType(['docx']) ? (
            <DocxViewer blobUrl={image!} />
          ) : (
            <img
              src={src ?? image}
              alt={alt}
              className="w-full h-auto object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Image
